import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, CheckCircle2, ChevronDown, Minus, LayoutGrid } from "lucide-react";
import axios from "axios";
import { ROOF_TENTS } from "@/data/categories";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { DemoBanner } from "@/components/DemoBanner";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (n ?? 0).toLocaleString("en-NZ");

// Renders "**bold**" markers inside a checklist body as bold white spans
const renderBoldSegments = (text) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="text-white font-semibold">{part}</strong> : part
  );

// The 3 real-world build markers that actually separate a genuine top-tier
// roof tent from an entry-level shell wearing a nicer sticker — referenced
// directly against the "One More Thing Worth Knowing" callout below it.
const REAL_BUILD_CHECKLIST = [
  { t: "Shell Construction", d: "**Plastic corner joints are fine on cheaper through to mid-range aluminium tent shells, but don't belong on true top tier designs.** Top-tier tent shells are fully aluminium — formed or welded into one structure, not assembled from side extrusions and top & bottom panels bonded together with plastic corner joints. That construction exists because it's cheap to manufacture. **Serious world brand tents skip the plastic corners in favour of genuinely original shell designs** built for a longer service life and considerably higher performance.\n\nBonded construction with plastic corner joints performs acceptably up to mid-priced roof tents, so we're not saying there isn't a place for them — our own SUB60 included, priced accordingly under $3k. It's worth questioning, though, when you see that same construction (or even the exact same shells) on tents priced above $5k." },
  { t: "Canvas Quality", d: "**Canvas of a quality you've simply not seen before on tents in NZ.** The video below shows one of our models built with authentic 400gsm South African woven canvas — roughly double the thickness and durability of the canvas found on most other tents — **then double-layered to an astounding 800gsm** — a genuinely different class compared to what's typically sold in NZ. Most other tents on the market use either lower grades of polycotton canvas, or sometimes just cheap polyester (we don't recommend that). Watch for canvas marketed under a technical-sounding or branded name — usually just polyester.\n\nCombine genuine South African canvas with real shell insulation (see next point) and the performance upgrade is substantial. You won't need the quilted liner add-on some brands offer — that's a workaround for tents built with lower-grade shells and canvas, not something a genuinely top-tier tent requires. Money saved on unnecessary accessories can be invested in a better tent." },
  { t: "Real Insulation, Not Anti-Condensation Mats", d: "**Buyers are often told to look for an anti-condensation mat beneath the mattress as a sign of a good tent — but genuine top-tier roof tents don't have one, because they don't need one.** Anti-condensation mats are a workaround for entry-level shells that are poorly insulated: they don't stop condensation, they just try to stop it soaking the mattress underneath.\n\n\"Insulated\" can mean very different things from one brand to the next — sometimes it's just a 1mm layer of felt, so that \"insulated\" can cheaply be claimed. Our top-tier brands use up to an inch of foam insulating the shell; this will clearly perform to a very different standard. Whatever insulation a tent has is hidden out of sight, so it pays to ask exactly what's actually in there rather than take the word at face value. Condensation is natural and can't be eliminated entirely — but our top-tier models reduce it drastically." },
];

// Roof tent mounting brackets + the wind deflector — these live under the Roof
// Racks category/URL (they clip onto a K9 rack), but shoppers browsing Roof
// Tents should see them too, right below the tent range itself. Fetched live
// from the roofrack API (not hardcoded) so their display order stays in sync
// with whatever order they're dragged into on the Roof Racks admin list.
const ROOFTENT_MOUNT_SLUGS = ["low-profile-roof-tent-mount-k9-163", "stealth-to-k9-fitting-kit", "top-hat-roof-tent-mounts-k9-163a", "quick-pitch-roof-tent-wind-deflector"];

// Curated hard-shell groupings for the store — editorial, not price-mechanical.
const FLAGSHIP_SLUGS = ["quick-pitch-hard-shell", "eezi-awn-xplor", "eezi-awn-blade-40th", "eezi-awn-stealth"];
const FAMILY_SLUGS = ["eezi-awn-sword-family", "quick-pitch-nyumba-family-size"];
const UNDER_5K_SLUGS = ["quick-pitch-nyumba", "eezi-awn-galaxy", "xpeditionz-sub60", "quick-pitch-aero"];

const Card = ({ m, testid }) => (
  <Link
    to={m.to || `/roof-tents/${m.slug}`}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
      <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
        {m.badge && (
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
            {m.badge}
          </span>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-xl md:text-2xl text-white leading-tight tracking-wide">{(m.name || "").toUpperCase()}</h3>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{m.tagline}</p>
        <div className="flex items-end justify-between gap-3 mt-4">
          <div>
            {m.priceWas && (
              <p className="font-mono text-[10px] text-white/40 line-through">${fmt(m.priceWas)}</p>
            )}
            <p className="font-display text-2xl text-[var(--brand-primary)] leading-none">
              {m.priceOnRequest ? (
                <span className="text-sm font-mono uppercase tracking-widest">Enquire</span>
              ) : (
                <>
                  {m.priceFrom && <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 mr-1">FROM</span>}
                  ${fmt(m.price)}
                </>
              )}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

export default function RoofTentsPage() {
  const [models, setModels] = useState(null); // null = still loading
  const [roofrackMounts, setRoofrackMounts] = useState([]);
  const [expandedChecklist, setExpandedChecklist] = useState({});
  const [worthKnowingDetailOpen, setWorthKnowingDetailOpen] = useState(false);
  const [shieldCheckOpen, setShieldCheckOpen] = useState(false);
  const [showStoreCTA, setShowStoreCTA] = useState(false);

  useEffect(() => {
    // Reveals the "Read enough? See the tents." sticky CTA once the visitor
    // has scrolled roughly past the hero, so it doesn't compete with it —
    // same threshold/pattern as the Wellside Canopies page's sticky CTA.
    const onScroll = () => setShowStoreCTA(window.scrollY > window.innerHeight * 0.85);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/products/rooftent`, { timeout: 15000 })
      .then((res) => setModels(res.data))
      .catch((err) => { console.error(err); setModels([]); });
    axios.get(`${API}/api/products/roofrack`).then((res) => {
      setRoofrackMounts(res.data.filter((p) => ROOFTENT_MOUNT_SLUGS.includes(p.slug)).map((p) => ({ ...p, to: `/roof-racks/${p.slug}` })));
    }).catch((err) => console.error(err));
  }, []);

  const hardShells = useMemo(() => (models || []).filter((m) => m.type === "hard-shell"), [models]);
  // Curated, editorial groupings (replaces the old mechanical "under/over $5k"
  // price split) — each slug is placed by hand so order within a group is
  // intentional, not auto-sorted by price.
  const flagshipModels = useMemo(
    () => FLAGSHIP_SLUGS.map((slug) => hardShells.find((m) => m.slug === slug)).filter(Boolean),
    [hardShells]
  );
  const familyModels = useMemo(
    () => FAMILY_SLUGS.map((slug) => hardShells.find((m) => m.slug === slug)).filter(Boolean),
    [hardShells]
  );
  const under5kModels = useMemo(
    () => UNDER_5K_SLUGS.map((slug) => hardShells.find((m) => m.slug === slug)).filter(Boolean),
    [hardShells]
  );
  const softShells = useMemo(() => (models || []).filter((m) => m.type === "soft-shell"), [models]);

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="roof-tents-page">

      {/* Sticky "read enough? see the tents" CTA — mirrors the Wellside
          Canopies page's sticky CTA. Desktop: compact bottom-left box (never
          sits on top of the Chatway bubble at bottom-right). Mobile:
          full-width bottom bar. Links to the top of the store (hard shells
          grid), so a visitor who's read enough can jump straight to it. */}
      <div
        data-testid="roof-tents-sticky-store-cta-desktop"
        className={`hidden md:flex fixed bottom-6 left-6 z-40 items-center gap-4 bg-[var(--bg-main)] border border-[var(--border-default)] border-l-4 border-l-[var(--brand-primary)] shadow-2xl px-6 py-4 transition-all duration-300 ${showStoreCTA ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
      >
        <span className="font-display text-lg text-white">Read enough? See the tents.</span>
        <a href="#hard-shells" data-testid="roof-tents-sticky-store-cta-link" className="btn-primary whitespace-nowrap">
          View Tents <ArrowUpRight size={14} />
        </a>
      </div>
      <div
        data-testid="roof-tents-sticky-store-cta-mobile"
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--bg-main)]/95 backdrop-blur-md border-t border-[var(--border-default)] px-4 flex transition-all duration-300 ${showStoreCTA ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        style={{ paddingTop: "0.75rem", paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <a
          href="#hard-shells"
          data-testid="roof-tents-sticky-store-cta-link-mobile"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[var(--brand-primary)] text-black text-xs uppercase tracking-wider py-3 font-semibold"
        >
          <LayoutGrid size={14} /> View Tents
        </a>
      </div>
      <SEO
        path="/roof-tents"
        title="Roof Tents NZ · EEZI-AWN · Quick Pitch · Hard & Soft Shell"
        description="Hard shell & soft shell roof tents from South Africa's leading brands — EEZI-AWN, Quick Pitch and XpeditioNZ SUB60. Full range, from $2,199 to $7,790 +GST. Backed by EEZI-AWN's 40-year expedition heritage."
        image={ROOF_TENTS.hero}
      />

      {/* HERO */}
      <section className="relative min-h-[75svh] md:min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={ROOF_TENTS.hero} alt="EEZI-AWN roof tent overland" className="w-full h-full object-cover" style={{ objectPosition: "60% center" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-8 md:pb-20 pt-32 md:pt-40 w-full">
          {/* Mobile: H1 leads right on the hero image, overline sits lower below it. Desktop: overline leads, H1 follows (unchanged) */}
          <div className="md:hidden">
            <h1 className="font-display text-3xl leading-[1.15] text-white hero-text-shadow max-w-[22ch] mb-4">
              A premium price should get you a <span className="text-[var(--brand-primary)]">genuinely premium build.</span>
            </h1>
            <p className="overline">Genuinely Original Design · Globally Proven South African Made Roof Tents</p>
          </div>
          <div className="hidden md:block">
            <p className="overline mb-6">Genuinely Original Design · Globally Proven South African Made Roof Tents</p>
            <h1 className="font-display text-4xl lg:text-5xl leading-[1.15] text-white hero-text-shadow max-w-3xl">
              A premium price should get you a <span className="text-[var(--brand-primary)]">genuinely premium build.</span>
            </h1>
            <div className="mt-8 max-w-2xl space-y-4">
              <p className="text-lg text-white/85 leading-relaxed">
                Xpeditionz is one of very few NZ suppliers offering genuine top-tier roof tents from established South African brands, proven around the world. An altogether different class from NZ brands.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile only: supporting paragraph sits below the hero image on the plain dark background (H1 + overline stay on the image itself, see above) */}
      <section className="md:hidden bg-[var(--bg-main)] container-x pb-6" style={{ marginTop: "-3px" }} data-testid="tents-hero-mobile-intro">
        <p className="text-lg text-white/85 leading-relaxed mb-4">
          Xpeditionz is one of very few NZ suppliers offering genuine top-tier roof tents from established South African brands, proven around the world. An altogether different class from NZ brands.
        </p>
      </section>

      {/* WORTH KNOWING BEFORE YOU BUY — simplify pass: this single section now carries the whole
          "plastic corners" proof + explanation that used to be spread across a separate lifestyle
          banner and a duplicate "badge" heading further down the page. */}
      <section className="container-x pt-10 md:pt-14 pb-12 md:pb-16" data-testid="roof-tents-worth-knowing">
        <div className="mb-8 max-w-3xl">
          <p className="overline overline-lg mb-3">— Worth Knowing Before You Buy</p>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
            PLASTIC CORNERS <span className="text-[var(--brand-primary)]">GIVE THE GAME AWAY.</span>
          </h2>
          <p className="text-[var(--text-secondary)] mt-5 leading-relaxed text-lg">
            There are two genuinely different classes of hard shell roof tent construction. The NZ market has largely only known the cheaper of the two.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          <div className="bg-[var(--bg-main)]" data-testid="shell-comparison-budget">
            <div className="px-5 md:px-6 py-4 border-b border-[var(--border-default)]">
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-white/60">Rebranded Generic — Our SUB60 and other local market roof tents</span>
            </div>
            <div className="aspect-[8/3] overflow-hidden">
              <img
                src="/products/rooftents/shell-comparison/budget-shell-corner.jpg"
                alt="Plastic corner joint on a budget aluminium tent shell"
                className="w-full h-full object-cover"
                width={796}
                height={298}
                loading="lazy"
                data-testid="shell-comparison-budget-photo"
              />
            </div>
            <ul className="p-5 md:p-6 space-y-2.5">
              {["Generic shell construction with plastic corner joints", "Often sold under several different local brands, including our own SUB60", "Mass-produced for low-cost assembly — solid value when priced accordingly", "Entry-level to mid-tier real-world performance", "Often marketed as \"designed locally for NZ conditions\" — worth asking exactly what engineering genuinely changed"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-lg text-[var(--text-secondary)]">
                  <Minus size={16} className="text-white/30 shrink-0 mt-[3px]" strokeWidth={2.2} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[var(--bg-main)]" data-testid="shell-comparison-premium">
            <div className="px-5 md:px-6 py-4 border-b border-[var(--border-default)]">
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-white/60">Original Manufacturer Design — Quick Pitch RTT</span>
            </div>
            <div className="aspect-[8/3] overflow-hidden">
              <img
                src="/products/rooftents/shell-comparison/premium-shell-corner.jpg"
                alt="Fully welded aluminium corner on a Quick Pitch hard shell roof tent"
                className="w-full h-full object-cover"
                width={798}
                height={298}
                loading="lazy"
                data-testid="shell-comparison-premium-photo"
              />
            </div>
            <ul className="p-5 md:p-6 space-y-2.5">
              {["South African made, Quick Pitch", "Genuinely original shell design — fully welded, no plastic corner joints, built by skilled tradesmen", "Foam-insulated shell, dual-layer authentic South African canvas up to 800gsm equivalent, mattresses up to 100mm thick — specs well beyond NZ brands", "Costs more, but far superior construction, performance, comfort and longevity"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-lg text-white">
                  <CheckCircle2 size={16} className="text-[var(--brand-primary)] shrink-0 mt-[3px]" strokeWidth={2.2} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-6 max-w-3xl text-white font-semibold leading-relaxed text-lg" data-testid="worth-knowing-lead-line">
          Look closely at the corner joints. They can tell you more about a roof tent's construction than the badge, price tag or marketing claims.
        </p>
        <p className="mt-4 max-w-3xl text-white/80 leading-relaxed text-lg" data-testid="worth-knowing-final-line">
          Tents built on generic shells can offer good value at the right price and meet many campers' needs. But those seeking a genuine step up to top-tier construction and performance should look beyond NZ-branded roof tents—our own SUB60 included.
        </p>
        <div className="mt-6 max-w-3xl">
          <button
            type="button"
            onClick={() => setWorthKnowingDetailOpen((v) => {
              if (v) { setExpandedChecklist({}); setShieldCheckOpen(false); }
              return !v;
            })}
            aria-expanded={worthKnowingDetailOpen}
            aria-controls="worth-knowing-detail-content"
            className="group w-full flex items-center justify-between gap-4 border border-[var(--border-default)] hover:border-[var(--brand-primary)] bg-[var(--bg-secondary)] px-5 md:px-6 py-4 md:py-5 text-left transition-colors"
            data-testid="worth-knowing-detail-toggle"
          >
            <div className="min-w-0">
              <div className="font-display text-xl md:text-2xl text-white leading-tight">
                {worthKnowingDetailOpen ? "Hide The Breakdown" : "What Actually Separates Top-Tier Construction"}
              </div>
              {!worthKnowingDetailOpen && (
                <div className="font-mono text-[10px] sm:text-sm uppercase tracking-normal text-white/50 mt-1.5 whitespace-nowrap overflow-hidden text-ellipsis">
                  <span className="sm:hidden">Shell · Canvas · Insulation — $5k+ Guide</span>
                  <span className="hidden sm:inline">Shell · Canvas · Insulation — what to know before you spend $5k+</span>
                </div>
              )}
            </div>
            <ChevronDown size={20} className={`shrink-0 text-[var(--brand-primary)] transition-transform ${worthKnowingDetailOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
        {worthKnowingDetailOpen && (
          <div className="mt-6" id="worth-knowing-detail-content" data-testid="worth-knowing-detail-content">
            <div className="max-w-3xl space-y-4">
              <p className="text-white/80 leading-relaxed text-lg">
                Plastic corner joints often identify a generic roof tent shell sold under several brand names. Our SUB60 uses one of these shells, and it's shared by other NZ brands, some priced thousands higher.
              </p>
              <p className="text-white/80 leading-relaxed text-lg">
                That's not a criticism of the shell. We've always positioned the SUB60 as offering mid-range construction and performance at a competitive price. Other NZ brands market tents built with the same shell as premium products, some even as "designed locally for NZ conditions." We've stayed away from such claims. Features and fittings differ, but a higher price for a tent built on the same shell doesn't mean better performance. It's worth questioning what you're actually paying for.
              </p>
              <p className="text-white/80 leading-relaxed text-lg">
                If your budget is over $5,000, you're approaching the price of our South African-made Quick Pitch RTT, with its welded aluminium shell, substantial insulation and dual-layer South African canvas. At that price, tents built on a generic shell with plastic corners become harder to justify. The Quick Pitch is a different class of tent altogether — not just the same tent with different marketing — and that's where your money is going. It's not a small upgrade.
              </p>
            </div>

            {/* WHAT TO ACTUALLY LOOK FOR — nested inside the outer Read More; each card keeps its own Read More toggle */}
            <div className="mt-10 md:mt-14 max-w-3xl">
              <p className="overline overline-lg mb-3">— Spending $5,000+</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                HERE'S WHAT YOU SHOULD BE LOOKING FOR <span className="text-[var(--brand-primary)]">— BUT WILL SELDOM FIND IN NZ.</span>
              </h2>
              <p className="text-white/80 leading-relaxed text-lg mt-4">
                Three things that separate a genuine top-tier roof tent from those built on generic entry-level shells.
              </p>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
              {REAL_BUILD_CHECKLIST.map((c, i) => {
                const match = c.d.match(/^\*\*(.+?)\*\*\s*([\s\S]*)$/);
                const lead = match ? match[1] : c.d;
                const rest = match ? match[2] : "";
                const isOpen = !!expandedChecklist[i];
                return (
                  <div key={c.t} data-testid={`real-build-checklist-${i}`} className="bg-[var(--bg-main)] p-6 md:p-7">
                    <CheckCircle2 size={20} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.8} />
                    <h3 className="font-display text-lg md:text-xl text-white mb-2 leading-snug">{c.t}</h3>
                    <p className="text-lg text-white leading-relaxed">{lead}</p>
                    {rest && (
                      <>
                        {isOpen && (
                          <div className="space-y-3 mt-3">
                            {rest.split(/\n\n+/).map((para, pi) => (
                              <p key={pi} className="text-lg text-white/80 leading-relaxed">{renderBoldSegments(para)}</p>
                            ))}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpandedChecklist((prev) => ({ ...prev, [i]: !prev[i] }))}
                          className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--brand-primary)] hover:text-white transition-colors"
                          data-testid={`real-build-checklist-toggle-${i}`}
                          aria-expanded={isOpen}
                        >
                          {isOpen ? "Read Less" : "Read More"}
                          <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* WHY GENUINE MATTERS — nested inside the outer Read More, with its own inner Read More for the detail */}
            <div className="mt-10 md:mt-14 border border-[var(--brand-primary)]/30 bg-[var(--bg-secondary)] p-6 md:p-10" data-testid="roof-tents-genuine-callout">
              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <ShieldCheck size={28} className="text-[var(--brand-primary)] shrink-0 mt-1" strokeWidth={1.6} />
                <div>
                  <p className="overline mb-3">— One More Thing Worth Knowing</p>
                  <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-4 max-w-2xl">
                    <em className="italic">"LOCALLY DESIGNED."</em> <em className="italic">"BUILT FOR NZ CONDITIONS."</em> WORTH ASKING WHAT THAT ACTUALLY MEANS.
                  </h3>
                  <p className="text-white/80 leading-relaxed max-w-3xl mb-3 text-lg">
                    Two of the most repeated phrases in the NZ roof tent market — here's what's actually behind them.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShieldCheckOpen((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--brand-primary)] hover:text-white transition-colors"
                    data-testid="shield-check-detail-toggle"
                    aria-expanded={shieldCheckOpen}
                  >
                    {shieldCheckOpen ? "Read Less" : "Read More"}
                    <ChevronDown size={14} className={`transition-transform ${shieldCheckOpen ? "rotate-180" : ""}`} />
                  </button>
                  {shieldCheckOpen && (
                    <div className="space-y-4 max-w-3xl mt-4">
                      <p className="text-white/80 leading-relaxed text-lg">
                        <strong className="text-white font-semibold">"Locally designed"</strong> usually means a local brand choosing things like canvas colour, spec level, or how many accessories — shoe bags and the like — come standard, on the same generic shell as our own SUB60 (see above). That's a real choice, but it's not the same as actually designing the tent. In a market that has largely only known rebranded generic tents, rather than the more seriously engineered world brands we carry, most Kiwi buyers simply haven't had anything else to compare against.
                      </p>
                      <p className="text-white/80 leading-relaxed text-lg">
                        <strong className="text-white font-semibold">"Built for NZ conditions"</strong> is the other claim worth scrutiny. If NZ's conditions genuinely required something different, you'd see it in the engineering — not just the cosmetic changes described above applied to a generic shell sold identically everywhere else. The XpeditioNZ team has planned and run global cross-continent 4WD expeditions — our last trip alone spanned 10 months, 16 countries, and elevations from sea level to near 5,000m, without swapping gear every time we crossed a border. The international 4WD overland community has long chosen the gear and brands that perform equally well everywhere — NZ included. Have a look at our{" "}
                        <Link to="/about" className="text-[var(--brand-primary)] underline hover:no-underline" data-testid="nz-conditions-about-link">About page</Link> if you want the full story.
                      </p>
                      <p className="text-white/80 leading-relaxed text-lg">
                        Worth asking exactly what sits behind either claim before paying more for it.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* CANVAS FEATURE VIDEO — grouped with the Worth Knowing proof content, ahead of the
          store, so it doesn't interrupt the Hard Shells → Soft Shells shopping flow. */}
      <section id="canvas-video" className="container-x pb-12 md:pb-16" data-testid="roof-tents-canvas-video-section">
        <div className="mb-8 max-w-2xl">
          <p className="overline mb-3">— See It In Action</p>
          <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
            THE CANVAS, <span className="text-[var(--brand-primary)]">UP CLOSE.</span>
          </h2>
          <p className="text-[var(--text-secondary)] mt-4 leading-relaxed text-lg">
            A closer look at the authentic South African canvas quality that separates a genuine EEZI-AWN or Quick Pitch tent from what's on the NZ market.
          </p>
        </div>
        <div className="relative w-full aspect-video border border-[var(--border-default)] overflow-hidden bg-black">
          <iframe
            src="https://www.youtube.com/embed/SbonHf97t_E?end=37"
            title="Roof Tent Canvas Features"
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="roof-tents-canvas-video-iframe"
          />
        </div>
      </section>

      {/* HARD SHELLS GRID */}
      <section id="hard-shells" className="container-x pt-10 md:pt-14 pb-12 md:pb-16 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— Hard Shells & Hybrids</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              IN STOCK, <span className="text-[var(--brand-primary)]">READY TO GO.</span>
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
        </div>

        {flagshipModels.length > 0 && (
          <div className="mb-10 border-l-4 border-[var(--brand-primary)] pl-4 md:pl-6" data-testid="hard-shells-flagship">
            <div className="flex items-center gap-2">
              <ShieldCheck size={22} className="text-[var(--brand-primary)] shrink-0" />
              <h3 className="font-display text-3xl md:text-4xl uppercase tracking-wide text-[var(--brand-primary)]">Our Flagship Roof Tents</h3>
            </div>
            <p className="text-[var(--text-secondary)] text-sm mt-1 mb-4">Genuine top-tier construction — a different class altogether.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {flagshipModels.map((m, i) => <Card key={m.slug} m={m} testid={`hard-shell-flagship-${i}`} />)}
            </div>
            <div className="mt-6 border border-[var(--border-default)] bg-[var(--bg-secondary)] py-5 px-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 text-center">
              <span className="text-white/80 text-sm md:text-base">Flagship prices a bit steep for your budget?</span>
              <a
                href="#hard-shells-under-5k"
                data-testid="hard-shells-under-5k-jump-link"
                className="btn-secondary"
              >
                Models Under $5,000 <ChevronDown size={14} />
              </a>
            </div>
          </div>
        )}

        {familyModels.length > 0 && (
          <div className="mb-10" data-testid="hard-shells-family">
            <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide text-white/60">Family Hard Shells</h3>
            <p className="text-[var(--text-secondary)] text-sm mt-1 mb-4">Bigger shells for bigger families — same construction standard, more sleeping space.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {familyModels.map((m, i) => <Card key={m.slug} m={m} testid={`hard-shell-family-${i}`} />)}
            </div>
          </div>
        )}

        {under5kModels.length > 0 && (
          <div id="hard-shells-under-5k" className="scroll-mt-24" data-testid="hard-shells-under-5k">
            <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide text-white/60">Options Under $5,000</h3>
            <p className="text-[var(--text-secondary)] text-sm mt-1 mb-4">Solid mid-range construction and performance for a tighter budget.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {under5kModels.map((m, i) => <Card key={m.slug} m={m} testid={`hard-shell-under5k-${i}`} />)}
            </div>
          </div>
        )}
      </section>

      {/* SOFT SHELLS GRID */}
      <section id="soft-shells" className="container-x py-12 md:py-16 border-t border-[var(--border-default)] scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— Soft Shell Models Available By Request</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              SOFT SHELL MODELS <span className="text-[var(--brand-primary)]">ON REQUEST.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-4 max-w-2xl leading-relaxed text-lg">
              EEZI-AWN's 40-year soft-shell lineage — the tents Africa's expedition fleets have been sleeping on for four decades. Real canvas, meranti hardwood boards. Not held as standard display stock, but available to order.
            </p>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {softShells.map((m, i) => <Card key={m.slug} m={m} testid={`soft-shell-${i}`} />)}
        </div>
      </section>

      {/* MOUNTS & WIND DEFLECTOR */}
      <section id="mounts" className="container-x pb-12 md:pb-16 border-t border-[var(--border-default)] pt-12 md:pt-16 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— Mounting Kit</p>
            <h2 className="font-display text-3xl md:text-4xl text-white leading-[0.95]">
              BRACKETS · <span className="text-[var(--brand-primary)]">WIND DEFLECTOR.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {roofrackMounts.map((m, i) => <Card key={m.to} m={m} testid={`roof-tent-mount-${i}`} />)}
        </div>
      </section>

      {/* SEE IT IN PERSON — "book a viewing" nudge, same pattern as Wellside/Bush Cabin/X-Series.
          Was previously missing on this page entirely; for a $2,199-$7,790 considered purchase,
          letting someone feel the canvas/shell in person is likely a strong conversion lever. */}
      <DemoBanner product="Roof Tent" image={hardShells[0]?.hero || hardShells[0]?.thumb} enquiryProduct="Roof Tent" />

      {/* GROUND TENTS CROSS-SELL */}
      <section className="container-x pb-12 md:pb-16">
        <Link
          to="/ground-tents"
          data-testid="cross-sell-ground-tents"
          className="group flex items-center justify-between gap-4 flex-wrap border border-[var(--border-default)] hover:border-[var(--brand-primary)] bg-[var(--bg-secondary)] px-6 py-6 md:px-10 md:py-8 transition-colors"
        >
          <div>
            <p className="overline mb-2">— Prefer To Camp On The Ground?</p>
            <h3 className="font-display text-xl md:text-2xl text-white">
              SHOP TENTCO <span className="text-[var(--brand-primary)]">SAFARI GROUND TENTS.</span>
            </h3>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors shrink-0">
            View Ground Tents <ArrowUpRight size={14} />
          </span>
        </Link>
      </section>
    </div>
  );
}
